import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = 'March 30, 2026';

  return (
    <main className="min-h-screen bg-white pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">

        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-400 mb-10">
          <Link to="/" className="hover:text-[#8B9B7E] transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-700">Privacy Policy</span>
        </nav>

        {/* Header */}
        <div className="mb-12">
          <p className="text-[#8B9B7E] text-sm font-medium tracking-widest uppercase mb-3">Legal</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#2B2B2B] mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm">Last updated: {lastUpdated}</p>
        </div>

        {/* Intro */}
        <div className="bg-[#f7f5f2] rounded-2xl p-6 mb-10">
          <p className="text-gray-700 text-sm leading-relaxed">
            Art Throng Home (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or use our home services in Goa and Mumbai.
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10 text-gray-700 text-sm leading-relaxed">

          <Section title="1. Information We Collect">
            <p>We may collect the following types of personal information:</p>
            <ul className="mt-3 space-y-2 list-none">
              <ListItem icon="ri-user-line">
                <strong>Identity &amp; Contact Data:</strong> Name, email address, phone number, and home address provided when you book a service or fill out a contact form.
              </ListItem>
              <ListItem icon="ri-calendar-line">
                <strong>Booking Data:</strong> Service preferences, scheduling information, and notes you submit during the booking process.
              </ListItem>
              <ListItem icon="ri-global-line">
                <strong>Technical Data:</strong> IP address, browser type, operating system, referring URLs, and pages visited, collected automatically via cookies and analytics tools.
              </ListItem>
              <ListItem icon="ri-mail-line">
                <strong>Marketing Data:</strong> Your preferences for receiving marketing materials from us.
              </ListItem>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="mt-3 space-y-2 list-none">
              <ListItem icon="ri-check-line">Provide, operate, and manage our home services</ListItem>
              <ListItem icon="ri-check-line">Process and confirm your bookings and appointments</ListItem>
              <ListItem icon="ri-check-line">Send you service confirmations, updates, and reminders</ListItem>
              <ListItem icon="ri-check-line">Respond to enquiries and customer support requests</ListItem>
              <ListItem icon="ri-check-line">Send promotional communications (with your consent)</ListItem>
              <ListItem icon="ri-check-line">Improve our website, services, and user experience</ListItem>
              <ListItem icon="ri-check-line">Comply with legal obligations</ListItem>
            </ul>
          </Section>

          <Section title="3. Cookies">
            <p>
              Our website uses cookies — small text files placed on your device — to enhance your browsing experience. We use:
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <CookieCard
                icon="ri-settings-3-line"
                title="Essential Cookies"
                desc="Required for the website to function correctly. Cannot be disabled."
              />
              <CookieCard
                icon="ri-bar-chart-line"
                title="Analytics Cookies"
                desc="Help us understand how visitors interact with our website (e.g., Google Analytics)."
              />
              <CookieCard
                icon="ri-megaphone-line"
                title="Marketing Cookies"
                desc="Used to deliver relevant ads and track the effectiveness of our campaigns."
              />
            </div>
            <p className="mt-4">
              You can control cookie preferences through the cookie banner displayed when you first visit our site, or by adjusting your browser settings. Declining non-essential cookies will not affect core website functionality.
            </p>
          </Section>

          <Section title="4. How We Share Your Information">
            <p>
              We do not sell or rent your personal data to third parties. We may share your information with:
            </p>
            <ul className="mt-3 space-y-2 list-none">
              <ListItem icon="ri-team-line">
                <strong>Service Providers:</strong> Trusted partners who assist in operating our website, processing bookings, or delivering services (e.g., scheduling platforms, email service providers), bound by confidentiality obligations.
              </ListItem>
              <ListItem icon="ri-shield-check-line">
                <strong>Legal Authorities:</strong> When required by law, court order, or governmental regulation.
              </ListItem>
              <ListItem icon="ri-building-line">
                <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction.
              </ListItem>
            </ul>
          </Section>

          <Section title="5. Data Retention">
            <p>
              We retain your personal data only as long as necessary to fulfil the purposes outlined in this policy, or as required by applicable law. Booking records are typically retained for up to 3 years for operational and legal purposes. You may request deletion of your data at any time (see Section 7).
            </p>
          </Section>

          <Section title="6. Data Security">
            <p>
              We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="7. Your Rights">
            <p>Depending on your location, you may have the right to:</p>
            <ul className="mt-3 space-y-2 list-none">
              <ListItem icon="ri-eye-line">Access the personal data we hold about you</ListItem>
              <ListItem icon="ri-edit-line">Request correction of inaccurate data</ListItem>
              <ListItem icon="ri-delete-bin-line">Request deletion of your personal data</ListItem>
              <ListItem icon="ri-forbid-line">Object to or restrict our processing of your data</ListItem>
              <ListItem icon="ri-download-line">Request a copy of your data in a portable format</ListItem>
              <ListItem icon="ri-mail-unread-line">Withdraw consent for marketing communications at any time</ListItem>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:info@artthronghome.com" className="text-[#8B9B7E] underline hover:text-[#7a8a6d] transition-colors">
                info@artthronghome.com
              </a>.
            </p>
          </Section>

          <Section title="8. Third-Party Links">
            <p>
              Our website may contain links to third-party websites (e.g., social media platforms). We are not responsible for the privacy practices or content of those sites, and we encourage you to review their privacy policies before providing any personal information.
            </p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>
              Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will update the &quot;Last updated&quot; date at the top of this page. We encourage you to review this policy periodically.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal data, please get in touch:
            </p>
            <div className="mt-4 bg-[#f7f5f2] rounded-xl p-5 space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-building-2-line text-[#8B9B7E]"></i>
                </div>
                <span className="font-medium text-[#2B2B2B]">Art Throng Home</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-map-pin-line text-[#8B9B7E]"></i>
                </div>
                <span>Panjim, Goa, India</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-mail-line text-[#8B9B7E]"></i>
                </div>
                <a href="mailto:info@artthronghome.com" className="text-[#8B9B7E] hover:underline transition-colors">
                  info@artthronghome.com
                </a>
              </div>
            </div>
          </Section>
        </div>

        {/* Back to Home */}
        <div className="mt-16 pt-10 border-t border-gray-100">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2B2B2B] hover:text-[#8B9B7E] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2B2B2B] mb-3">
        <a id={title.replace(/\s+/g, '-').toLowerCase()}>{title}</a>
      </h4>
      {children}
    </section>
  );
}

function ListItem({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
        <i className={`${icon} text-[#8B9B7E] text-base`}></i>
      </div>
      <span>{children}</span>
    </li>
  );
}

function CookieCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4">
      <div className="w-8 h-8 flex items-center justify-center mb-2">
        <i className={`${icon} text-[#8B9B7E] text-lg`}></i>
      </div>
      <p className="font-medium text-[#2B2B2B] text-sm mb-1">{title}</p>
      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}
